import { Team } from '@optools/services/statsapi/types';

import { findTeamById } from './filter';

/**
 * Hydrates entities with team information by looking up teams based on teamId.
 *
 * Takes a collection of entities that may have a teamId property and enriches
 * each entity with the corresponding team object. The results are sorted
 * alphabetically by team name.
 *
 * @template T - Entity type that may contain a teamId property
 * @param items - Array of entities to hydrate with team data
 * @param teams - Array of teams to use for lookup
 * @returns Array of hydrated entities with team property added, sorted by team name
 *
 * @example
 * const configs = [{ id: 1, teamId: 147 }, { id: 2, teamId: 121 }];
 * const hydrated = hydrateWithTeams(configs, teams);
 * // Returns: [{ id: 1, teamId: 147, team: { id: 147, name: "Yankees", ... } }, ...]
 */
export function hydrateWithTeams<T extends { teamId?: number | null | undefined }>(
  items: T[],
  teams: Team[],
) {
  return items
    .map((item) => ({
      ...item,
      team: item.teamId ? findTeamById(teams, item.teamId) : undefined,
    }))
    .toSorted((a, b) => {
      const teamAName = a.team?.name ?? '';
      const teamBName = b.team?.name ?? '';
      return teamAName.localeCompare(teamBName);
    });
}
